import { useEffect, useMemo, useState } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { formatNumber } from '../../../utils/util';

interface tableDataProps {
  arrivedAt: string;
  dayType: string;
  amount: number;
  tax: number;
  note?: string;
}
// amount: 350000;
// arrivedAt: '09/03';
// dayType: 'full';
// id: 9;

export const TableComponent = ({
  data,
  setConfirmModal,
  onUpdateNotes,
}: any) => {
  const { register, handleSubmit } = useForm();
  const [notes, setNotes] = useState<{ [key: number]: string }>({}); // 각 행에 대한 note 상태 관리

  const onSubmit = (data: any) => {
    setConfirmModal(true);
    onUpdateNotes(data); // 사용자 입력한 note 값을 상위 컴포넌트로 전달

    console.log(data);
  };

  // 금액 합계 및 세액 합계를 계산하는 useMemo
  const totalCost = useMemo(
    () =>
      data.reduce((sum: number, row: tableDataProps) => sum + row.amount, 0),
    [data],
  );

  const totalTaxCost = useMemo(
    () =>
      data.reduce(
        (sum: number, row: tableDataProps) => sum + row.amount * 0.1,
        0,
      ), // tax는 amount의 10%로 계산
    [data],
  );

  const columns = useMemo<ColumnDef<tableDataProps>[]>(
    () => [
      {
        header: '날짜',
        accessorKey: 'arrivedAt',
        cell: (info) => info.getValue(),
      },
      {
        header: '형태',
        accessorKey: 'dayType',
        cell: (info) => {
          const value = info.getValue() as string;

          // 'full'을 '전일'로, 'half'를 '반일'로 치환
          const replacedValue = value
            .replace(/full/g, '전일') // 'full'이 포함된 경우 '전일'로 변경
            .replace(/half/g, '반일'); // 'half'이 포함된 경우 '반일'로 변경

          // 변환된 값을 반환
          return replacedValue;
        },
      },
      {
        header: '금액',
        accessorKey: 'amount',
        cell: (info) => <span>{formatNumber(Number(info.getValue()))}</span>,
      },
      {
        header: '세액',
        accessorKey: 'tax',
        cell: (info) => {
          const amount = info.row.original.amount; // amount 값을 가져옴
          const tax = amount * 0.1; // 10% 세액 계산
          return <span>{formatNumber(Number(tax))}</span>; // 계산된 세액 출력
        },
      },
      {
        header: '비고',
        cell: (info) => {
          const rowIndex = info.row.index;
          return (
            <div className="table-input">
              <input
                type="text"
                {...register(`note-${rowIndex}` as const)} // 비고 값 등록
                onChange={(e) => {
                  const newNotes = { ...notes, [rowIndex]: e.target.value };
                  setNotes(newNotes); // note 값을 상태에 저장
                }}
              />
            </div>
          );
        },
      },
    ],
    [register],
  );
  const table = useReactTable({
    data,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });
  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <table>
          <thead>
            {table.getHeaderGroups().map((item) => (
              <tr key={item.id}>
                {item.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: `${header.getSize()}px` }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    //className={cell.column.columnDef.meta?.cellClassName}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={2}>합계</td>
              <td>{formatNumber(totalCost)}</td>
              <td>세액</td>
              <td>{formatNumber(totalTaxCost)}</td>
            </tr>
          </tfoot>
        </table>
        <ConfirmBtn type="submit">다운로드</ConfirmBtn>
      </form>
    </Container>
  );
};

// style
const Container = styled.section`
  max-width: calc(100vw - 2.8rem);
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 11.4rem;
  form {
    table {
      margin-top: 2.4rem;
      width: 100%;
      font-size: 1.3rem;
      line-height: 1;
      letter-spacing: -5%;
      thead {
        border-top: 2px solid #222;
        th {
          background-color: ${(props) => props.theme.textWhiteColor};
        }
      }
      th,
      td {
        padding: 1.8rem 1rem;
        border-bottom: 1px solid #f0f0f0;
        text-align: center;
        white-space: nowrap;
        .datail-btn {
          text-decoration: underline;
          cursor: pointer;
        }
      }
      tbody {
        td {
          padding: 0.25rem 0.23rem;
        }
      }
      td.sm-padding {
        padding: 0.9rem 0;
      }
      .left-align {
        text-align: left;
      }
      .px-1 {
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .table-input {
        input {
          border: 1px solid ${(props) => props.theme.searchBoxBorderedColor};
          font-size: 1.3rem;
          height: 2.8rem;
          border-radius: 0.8rem;
          max-width: 6rem;
        }
      }
      tfoot {
        background-color: ${(props) => props.theme.costBgColor};
        font-weight: 600;
        tr {
          td:first-child {
            text-align: left;
            padding-left: 2rem;
          }
        }
      }
    }
  }
`;

const ConfirmBtn = styled.button`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) => props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;
